// src/encounter/lib/allied-soap.ts
// [PROD-3376] Allied health · the note in the clinician's SOAP order, one card per template section.
//
// The allied-health steps are Risk · Docs · Plan · Wrap-up, so every section a dietitian template
// authors under `risk-` drew as ONE Risk card in Subjective: the whole visit on one page (Samer, 2 Nov:
// "it needs to be reconstructed around the mental model of any clinician: subjective, objective,
// assessment, plan"). The template now says where each section belongs, with the standard LOINC
// note-section code on the section's top-level group (`item.code`, the same codes C-CDA uses for its
// SOAP sections). This file reads those codes. Nothing here changes a step, a save path or a role:
//
//   - A top-level Risk group coded with a section code is drawn as its OWN card in that section.
//   - A group with no section code stays in the Risk card, exactly as before.
//   - Only allied health reads the codes. The physician and the nurse are untouched.
//   - A template with no coded group changes nothing (the Assessment tab appears only when a group
//     is coded Assessment, e.g. the dietitian's nutrition diagnosis — a nutrition diagnosis, not a
//     medical one, so PROD-2040's "no A for allied health" still holds for every other template).
//
// Pure; never throws.
import type { Questionnaire, QItem } from '@scripsteam/scrips-signal-ds';
import type { ClinicalRole } from './encounter-blocks';
import { linkIdInSection } from './questionnaire-groups';
import type { SoapSectionId } from './soap-ribbon';

/** LOINC note-section codes → the note section they open (LOINC; C-CDA SOAP section codes). */
export const SOAP_SECTION_CODES: Readonly<Record<string, SoapSectionId>> = {
  '61150-9': 's', // Subjective Narrative
  '61149-1': 'o', // Objective Narrative
  '51848-0': 'a', // Assessment (Evaluation note)
  '18776-5': 'plan', // Plan of care note
};

const isLoinc = (system: string | undefined): boolean =>
  (system ?? '')
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/\/+$/, '') === 'loinc.org';

/** The note section a top-level group's code places it in, or `undefined` (no section code). */
export function soapSectionOfGroup(group: QItem): SoapSectionId | undefined {
  for (const c of group.code ?? []) {
    const id = isLoinc(c.system) ? SOAP_SECTION_CODES[(c.code ?? '').trim()] : undefined;
    if (id) return id;
  }
  return undefined;
}

export interface AlliedSoapLayout {
  /** The coded Risk groups of each section, in the template's order. */
  groups: Readonly<Partial<Record<SoapSectionId, readonly QItem[]>>>;
  /** Every coded group's linkId (the Risk card no longer draws these). */
  coded: ReadonlySet<string>;
}

const NONE: AlliedSoapLayout = { groups: {}, coded: new Set() };

/** The allied-health SOAP layout of a template. Any other role, or no coded group → nothing. */
export function alliedSoapLayout(questionnaire: Questionnaire | undefined, role: ClinicalRole | string): AlliedSoapLayout {
  if (role !== 'allied-health' || !questionnaire) return NONE;
  const groups: Partial<Record<SoapSectionId, QItem[]>> = {};
  const coded = new Set<string>();
  for (const g of questionnaire.item ?? []) {
    if (g.type !== 'group' || !linkIdInSection(g.linkId, 'risk')) continue;
    const id = soapSectionOfGroup(g);
    if (!id) continue;
    (groups[id] ??= []).push(g);
    coded.add(g.linkId);
  }
  return coded.size === 0 ? NONE : { groups, coded };
}

/** The sections the coded groups add to the ribbon (only Assessment can be missing for allied health). */
export function alliedExtraSections(layout: AlliedSoapLayout): SoapSectionId[] {
  return (layout.groups.a?.length ?? 0) > 0 ? ['a'] : [];
}

/** The questionnaire with only these top-level groups (a card's own section). */
export function withGroups(questionnaire: Questionnaire, keep: (g: QItem) => boolean): Questionnaire {
  return { ...questionnaire, item: questionnaire.item.filter(keep) };
}

export interface AlliedSection {
  /** This section's coded groups, in the template's order (one card each). */
  groups: readonly QItem[];
  /** The Risk card's own questionnaire once the coded groups draw apart; undefined → unchanged. */
  riskQuestionnaire?: Questionnaire;
  /** Nothing is left for the Risk card: the step draws no card of its own. */
  dropRisk: boolean;
  /** Objective holds a coded group: the Vitals block opens it. */
  vitals: boolean;
}

const NO_GROUPS: readonly QItem[] = [];

/** What one note section draws for allied health's coded groups (pure: the note-section component only renders it). */
export function alliedSection(layout: AlliedSoapLayout, questionnaire: Questionnaire | undefined, sectionId: SoapSectionId): AlliedSection {
  const groups = layout.groups[sectionId] ?? NO_GROUPS;
  const riskQuestionnaire = layout.coded.size > 0 && questionnaire ? withGroups(questionnaire, (g) => !layout.coded.has(g.linkId)) : undefined;
  return { groups, riskQuestionnaire, dropRisk: !!riskQuestionnaire && riskQuestionnaire.item.length === 0, vitals: sectionId === 'o' && groups.length > 0 };
}
